import React from "react";

import mountainPeak from "../../../img/mountainPeak.jpg";

/**
 * Component previews for the add-to-dashboard drawer.
 *
 * These are how you recognise a component before it is on your board, so they
 * are the real thing at small scale — not a hint of it.
 *
 * Three rules, learned the hard way:
 *
 * 1. THEY CARRY REAL TYPE. The previous versions were deliberately text-free
 *    and drew a short grey bar wherever a number belonged. At the size they
 *    were rendered (a fixed 152×86 box beside the text) that was the only
 *    option, and the result read as a sketch of a component rather than a
 *    component. The drawer now gives the preview the full panel width above
 *    the text — ~336×150, near four times the area — which is enough for
 *    names and figures at 9–11px.
 *
 * 2. NEVER preserveAspectRatio="none". Stretching the art to whatever box it
 *    lands in is what made circles into ovals and type into slabs. Every
 *    preview keeps its aspect and letterboxes instead.
 *
 * 3. `tiny` for the scene tiles. The editor shows these at ~150×50 to pick a
 *    scene; type at that scale is mush, so `tiny` drops the labels and lets
 *    the shapes carry it. Nothing else changes.
 *
 * Brand blue comes through --gmbw-primary so previews follow the company's
 * colour like the rest of the dashboard.
 */

const BOX = { display: "block", width: "100%", height: "100%", fontFamily: '"DM Sans", "Inter", "Arial", sans-serif' };
const BRAND = "var(--gmbw-primary, #1b598e)";
const YETI = "#EC2786";
const INK = "#0F172A";
const FAINT = "#98A2B3";
const GREEN = "#0F8A50";
const RED = "#D92D20";

/* One frame for every preview. 2.4:1 matches the drawer's preview box, so the
   art fills it without letterboxing there, and letterboxes gracefully anywhere
   else. */
const W = 300;
const H = 125;
const VB = `0 0 ${W} ${H}`;

const Svg = ({ children }) => (
  <svg viewBox={VB} preserveAspectRatio="xMidYMid meet" style={BOX} aria-hidden="true">
    <rect width={W} height={H} fill="#fff" />
    {children}
  </svg>
);

/* ── Essentials ─────────────────────────────────────────────────────────── */

export const PreviewHero = ({ tiny }) => {
  const cx = W / 2;
  const r = tiny ? 22 : 20;
  const cy = tiny ? H / 2 : 34;
  return (
    <Svg>
      <circle cx={cx} cy={cy} r={r} fill="#E7EDF4" />
      <circle cx={cx} cy={cy - r * 0.24} r={r * 0.38} fill="#B9C7D6" />
      <path d={`M${cx - r * 0.62} ${cy + r * 0.82} a ${r * 0.62} ${r * 0.55} 0 0 1 ${r * 1.24} 0 z`} fill="#B9C7D6" />
      {!tiny && (
        <>
          <text x={cx} y="66" textAnchor="middle" fontSize="10" fill={FAINT}>Good Morning</text>
          <text x={cx} y="82" textAnchor="middle" fontSize="15.5" fontWeight="600" fill={INK} letterSpacing="-.3">
            Justin Morse
          </text>
          <rect x={cx - 63} y="94" width="126" height="7" rx="3.5" fill="#EFF3F7" />
          <rect x={cx - 63} y="94" width="86" height="7" rx="3.5" fill={BRAND} />
          <text x={cx - 63} y="114" fontSize="8.4" fontWeight="600" fill={BRAND}>Level 7</text>
          <text x={cx + 63} y="114" textAnchor="end" fontSize="8.4" fill={FAINT}>Rank 1 of 12</text>
        </>
      )}
    </Svg>
  );
};

export const PreviewWidgets = ({ tiny }) => {
  const rows = [
    ["Quote of the day", "“Small hinges swing big doors.”", "#EEF4FB", BRAND],
    ["Current streak", "6 days", "#F0F8F3", "#12A150"],
    ["Next milestone", "$250K", "#FBF3EE", "#B54708"],
  ];
  const pad = 11;
  const rh = (H - 14) / 3;
  return (
    <Svg>
      {rows.map((t, i) => {
        const y = 8 + i * rh;
        const ch = rh - 6;
        return (
          <g key={t[0]}>
            <rect x={pad} y={y} width={W - pad * 2} height={ch} rx="7" fill="#FBFCFD" stroke="#EEF1F5" />
            <rect x={pad + 8} y={y + ch / 2 - 8} width="16" height="16" rx="5" fill={t[2]} />
            <circle cx={pad + 16} cy={y + ch / 2} r="3.4" fill={t[3]} />
            {!tiny && (
              <>
                <text x={pad + 32} y={y + ch / 2 - 1} fontSize="7.8" fill={FAINT}>{t[0]}</text>
                <text x={pad + 32} y={y + ch / 2 + 11} fontSize="9.6" fontWeight="600" fill={INK}>{t[1]}</text>
              </>
            )}
          </g>
        );
      })}
    </Svg>
  );
};

/* ── Performance ───────────────────────────────────────────────────────── */

/* The ruler down the right edge, shared by both King of the Mountain scenes —
   it is the thing that makes the card readable as a scoreboard. */
const Ruler = ({ x, colour, opacity = 1 }) => (
  <>
    <line x1={x} y1="18" x2={x} y2={H - 14} stroke={colour} strokeWidth="1.3" opacity={opacity} />
    {Array.from({ length: 13 }, (_, i) => {
      const y = 18 + i * ((H - 32) / 12);
      const major = i % 3 === 0;
      return (
        <line
          key={y}
          x1={major ? x - 9 : x - 5}
          y1={y}
          x2={x}
          y2={y}
          stroke={colour}
          strokeWidth={major ? 1.3 : 0.8}
          opacity={(major ? 1 : 0.55) * opacity}
        />
      );
    })}
  </>
);

export const PreviewKotm = ({ tiny }) => (
  <Svg>
    <defs>
      <linearGradient id="gmbwKotmSky" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#EAF3FB" />
        <stop offset="1" stopColor="#FAFCFE" />
      </linearGradient>
    </defs>
    <rect width={W} height={H} fill="url(#gmbwKotmSky)" />
    <path d={`M0 ${H} L38 76 L76 ${H} Z`} fill="#E1EBF4" />
    <path d={`M${W} ${H} L${W - 46} 66 L${W - 92} ${H} Z`} fill="#E1EBF4" />
    <path d={`M26 ${H} L118 16 L210 ${H} Z`} fill="#CBDEEE" />
    <path d={`M118 16 L210 ${H} L118 ${H} Z`} fill="#B4CCE2" />
    <path d="M118 16 l11 17 -6 -2 -5 5 -5 -5 -6 2 z" fill="#FFFFFF" />
    <path
      d="M56 116 C84 100 78 78 100 60 C114 48 116 30 117 21"
      fill="none"
      stroke="#FFFFFF"
      strokeWidth="2"
      strokeDasharray="6 4.5"
      strokeLinecap="round"
    />
    <path d="M112 4 h11 v4.2 a5.5 5.5 0 0 1 -11 0 z" fill="#E0A62E" />
    <rect x="116.5" y="11" width="2.2" height="3.4" fill="#C08E22" />
    <line x1="104" y1="56" x2={W - 32} y2="56" stroke={YETI} strokeWidth="1.2" opacity=".7" />
    <line x1="100" y1="66" x2={W - 32} y2="66" stroke={BRAND} strokeWidth="1.2" opacity=".7" />
    <circle cx="104" cy="56" r="6" fill={YETI} stroke="#fff" strokeWidth="1.8" />
    <circle cx="100" cy="66" r="6" fill={BRAND} stroke="#fff" strokeWidth="1.8" />
    <Ruler x={W - 32} colour={BRAND} />
    {!tiny && (
      <>
        <text x={W - 26} y="59" fontSize="9" fontWeight="600" fill={YETI}>4,105</text>
        <text x={W - 26} y="69" fontSize="9" fontWeight="600" fill={BRAND}>4,820</text>
      </>
    )}
  </Svg>
);

export const PreviewKotmPeak = ({ tiny }) => (
  <Svg>
    {/* the actual asset the real photo mountain renders — src/img/mountainPeak.jpg */}
    <image href={mountainPeak} x="0" y={-H * 0.12} width={W} height={H * 1.24} preserveAspectRatio="xMidYMid slice" />
    {/* the trail up to the summit */}
    <path
      d={`M${W * 0.16} ${H * 0.92} C${W * 0.3} ${H * 0.76} ${W * 0.29} ${H * 0.56} ${W * 0.42} ${H * 0.42} C${W * 0.5} ${H * 0.33} ${W * 0.53} ${H * 0.22} ${W * 0.55} ${H * 0.16}`}
      fill="none"
      stroke="#FFFFFF"
      strokeWidth="2.2"
      strokeDasharray="6 5"
      strokeLinecap="round"
      opacity="0.95"
    />
    {/* the two climbers on the mountain */}
    <circle cx={W * 0.44} cy={H * 0.4} r="6.5" fill={YETI} stroke="#fff" strokeWidth="2" />
    <circle cx={W * 0.4} cy={H * 0.5} r="6.5" fill={BRAND} stroke="#fff" strokeWidth="2" />
    {/* trophy on the peak */}
    <path d={`M${W * 0.53} ${H * 0.08} h12 v4.6 a6 6 0 0 1 -12 0 z`} fill="#E0A62E" />
    <Ruler x={W - 26} colour="#FFFFFF" opacity={0.95} />
    {!tiny && (
      <>
        <text x={W - 34} y={H * 0.42} textAnchor="end" fontSize="9" fontWeight="600" fill="#FFFFFF">4,105</text>
        <text x={W - 34} y={H * 0.52} textAnchor="end" fontSize="9" fontWeight="600" fill="#FFFFFF">4,820</text>
      </>
    )}
  </Svg>
);

export const PreviewYeti = ({ tiny }) => {
  const rows = [
    ["Estimates won", "$412K", 0.66, BRAND],
    ["Memberships", "38", 0.78, YETI],
    ["Avg ticket", "$1,180", 0.47, BRAND],
  ];
  const paceX = W * 0.74;
  const top = tiny ? 8 : 22;
  const rh = (H - top - 10) / 3;
  const barX = tiny ? 12 : 98;
  const barW = W - barX - (tiny ? 12 : 54);
  return (
    <Svg>
      <line x1={paceX} y1={top - 5} x2={paceX} y2={H - 8} stroke="#D3DEE9" strokeDasharray="3 3" />
      {!tiny && (
        <text x={paceX} y="12" textAnchor="middle" fontSize="7.8" fontWeight="600" fill="#AEBAC7">Yeti pace</text>
      )}
      {rows.map((r, i) => {
        const cy = top + rh * i + rh / 2;
        return (
          <g key={r[0]}>
            {!tiny && <text x="12" y={cy + 3.4} fontSize="9.4" fontWeight="500" fill="#5A6675">{r[0]}</text>}
            <rect x={barX} y={cy - 6.5} width={barW} height="13" rx="6.5" fill="#EFF3F7" />
            <rect x={barX} y={cy - 6.5} width={barW * r[2]} height="13" rx="6.5" fill={r[3]} />
            {!tiny && <text x={W - 11} y={cy + 3.4} textAnchor="end" fontSize="9.6" fontWeight="600" fill={INK}>{r[1]}</text>}
          </g>
        );
      })}
    </Svg>
  );
};

export const PreviewBeatYeti = ({ tiny }) => {
  const tiles = [
    ["Estimates won", "$412K", "+8%", true, 0.62, 0.5],
    ["Memberships", "38", "+12%", true, 0.7, 0.44],
    ["Avg ticket", "$1,180", "−4%", false, 0.4, 0.58],
  ];
  const pad = 10;
  const gap = 8;
  const cw = (W - pad * 2 - gap * 2) / 3;
  const mTop = tiny ? 6 : 18;
  const mH = tiny ? H - mTop - 6 : 46;
  return (
    <Svg>
      {!tiny && (
        <>
          <circle cx={W - 68} cy="8" r="3.4" fill={BRAND} />
          <text x={W - 61} y="11" fontSize="7.8" fill={FAINT}>You</text>
          <circle cx={W - 35} cy="8" r="3.4" fill={YETI} />
          <text x={W - 28} y="11" fontSize="7.8" fill={FAINT}>Yeti</text>
        </>
      )}
      {tiles.map((t, i) => {
        const x = pad + i * (cw + gap);
        const base = mTop + mH;
        const px = x + cw * 0.5 - cw * 0.3 * (1 - t[4]);
        const py = base - 2 - (mH - 8) * t[4];
        const yx = x + cw * 0.5 + cw * 0.3 * (1 - t[5]);
        const yy = base - 2 - (mH - 8) * t[5];
        return (
          <g key={t[0]}>
            <rect x={x} y={mTop} width={cw} height={mH} rx="6" fill="#F7FAFC" stroke="#EDF1F5" />
            <path d={`M${x + cw * 0.12} ${base - 2} L${x + cw * 0.5} ${mTop + 6} L${x + cw * 0.88} ${base - 2} Z`} fill="#DDE7F0" />
            <path d={`M${x + cw * 0.5} ${mTop + 6} L${x + cw * 0.88} ${base - 2} L${x + cw * 0.5} ${base - 2} Z`} fill="#CBD9E7" />
            <circle cx={yx} cy={yy} r="4.6" fill={YETI} stroke="#fff" strokeWidth="1.6" />
            <circle cx={px} cy={py} r="4.6" fill={BRAND} stroke="#fff" strokeWidth="1.6" />
            {!tiny && (
              <>
                <text x={x} y={base + 14} fontSize="8.2" fill={FAINT}>{t[0]}</text>
                <text x={x} y={base + 28} fontSize="11.5" fontWeight="600" fill={INK}>{t[1]}</text>
                <text x={x + cw} y={base + 28} textAnchor="end" fontSize="8.8" fontWeight="600" fill={t[3] ? GREEN : "#B42318"}>
                  {t[2]}
                </text>
              </>
            )}
          </g>
        );
      })}
    </Svg>
  );
};

/* Scene 2 of Beat the Yeti — the same metrics as light pass/fail "gates". */
export const PreviewBeatYetiGates = ({ tiny }) => {
  const gates = [
    ["Close rate", "42%", "close", 0.72, "#F04438"],
    ["Quality", "97%", "beat", 1, "#12A150"],
    ["Avg ticket", "$2,850", "close", 0.62, "#F04438"],
    ["Memberships", "14", "pace", 0.7, "#F79009"],
  ];
  const pad = 10;
  const gap = 8;
  const cw = (W - pad * 2 - gap) / 2;
  const ch = tiny ? (H - pad * 2 - gap) / 2 : 44;
  return (
    <Svg>
      {gates.map((g, i) => {
        const x = pad + (i % 2) * (cw + gap);
        const y = pad + Math.floor(i / 2) * (ch + gap);
        return (
          <g key={g[0]}>
            <rect x={x} y={y} width={cw} height={ch} rx="7" fill="#fff" stroke={g[4]} strokeOpacity="0.4" />
            {!tiny && (
              <>
                <text x={x + 10} y={y + 15} fontSize="7.6" fill={FAINT} style={{ letterSpacing: "0.04em" }}>{g[0]}</text>
                <text x={x + 10} y={y + 30} fontSize="12.5" fontWeight="700" fill={INK}>{g[1]}</text>
              </>
            )}
            <rect x={x + 10} y={y + ch - 9} width={cw - 20} height="3.4" rx="1.7" fill="#EEF1F4" />
            <rect x={x + 10} y={y + ch - 9} width={(cw - 20) * g[3]} height="3.4" rx="1.7" fill={g[4]} />
          </g>
        );
      })}
    </Svg>
  );
};

/* ── People ────────────────────────────────────────────────────────────── */

export const PreviewLeaderboard = ({ tiny }) => {
  const rows = [
    ["1", "JM", "Justin Morse", "4,820", 1, true],
    ["2", "KR", "Kyle Ramos", "4,105", 0.85, false],
    ["3", "DP", "Dana Pike", "3,540", 0.73, false],
    ["4", "MS", "Marco Silva", "2,910", 0.6, false],
  ];
  const top = 10;
  const rh = (H - top - 8) / 4;
  const ax = tiny ? 18 : 28;
  const barX = tiny ? 34 : 138;
  const barW = W - barX - (tiny ? 12 : 54);
  return (
    <Svg>
      {rows.map((r, i) => {
        const cy = top + rh * i + rh / 2;
        const me = r[5];
        return (
          <g key={r[2]}>
            {!tiny && <text x="10" y={cy + 3.4} fontSize="9" fontWeight="600" fill={FAINT}>{r[0]}</text>}
            <circle cx={ax} cy={cy} r="8.6" fill={me ? BRAND : "#E4EBF2"} />
            {!tiny && (
              <>
                <text x={ax} y={cy + 3.1} textAnchor="middle" fontSize="7.6" fontWeight="700" fill={me ? "#fff" : "#8494A6"}>
                  {r[1]}
                </text>
                <text x="42" y={cy + 3.6} fontSize="10.2" fontWeight={me ? 600 : 500} fill={me ? INK : "#5A6675"}>
                  {r[2]}
                </text>
              </>
            )}
            <rect x={barX} y={cy - 5} width={barW} height="10" rx="5" fill="#F0F4F8" />
            <rect x={barX} y={cy - 5} width={barW * r[4]} height="10" rx="5" fill={BRAND} opacity={me ? 1 : 0.34} />
            {!tiny && (
              <text x={W - 10} y={cy + 3.6} textAnchor="end" fontSize="9.6" fontWeight="600" fill={me ? INK : "#7E8B99"}>
                {r[3]}
              </text>
            )}
          </g>
        );
      })}
    </Svg>
  );
};

export const PreviewPodium = ({ tiny }) => {
  const spots = [
    { x: 0.2, ped: 36, r: 12, ini: "KR", nm: "Kyle", pts: "4,105", me: false },
    { x: 0.5, ped: 48, r: 14, ini: "JM", nm: "Justin", pts: "4,820", me: true },
    { x: 0.8, ped: 28, r: 12, ini: "DP", nm: "Dana", pts: "3,540", me: false },
  ];
  const base = H - 6;
  const pw = W * 0.21;
  return (
    <Svg>
      {spots.map((p) => {
        const cx = W * p.x;
        const py = base - p.ped;
        const cy = py - p.r - 6;
        return (
          <g key={p.ini}>
            <circle cx={cx} cy={cy} r={p.r} fill={p.me ? BRAND : "#E4EBF2"} />
            {!tiny && (
              <text x={cx} y={cy + 3.8} textAnchor="middle" fontSize="8.6" fontWeight="700" fill={p.me ? "#fff" : "#8494A6"}>
                {p.ini}
              </text>
            )}
            {p.me && (
              <path d={`M${cx - 8} ${cy - p.r - 5} l3.4 4 3.4-5.4 3.4 5.4 3.4-4 1 6h-15.6z`} fill="#E0A62E" />
            )}
            <rect
              x={cx - pw / 2}
              y={py}
              width={pw}
              height={p.ped}
              rx="4"
              fill={p.me ? BRAND : "#EDF1F6"}
              opacity={p.me ? 0.15 : 1}
            />
            {!tiny && (
              <>
                <text x={cx} y={py + 14} textAnchor="middle" fontSize="9.8" fontWeight="600" fill={p.me ? BRAND : "#6B7787"}>
                  {p.nm}
                </text>
                <text x={cx} y={py + 27} textAnchor="middle" fontSize="8.8" fontWeight="600" fill={p.me ? BRAND : "#94A2B1"}>
                  {p.pts}
                </text>
              </>
            )}
          </g>
        );
      })}
    </Svg>
  );
};

/* ── Sales ─────────────────────────────────────────────────────────────── */

/* The two earnings scenes, drawn as real miniatures of the card (never grey
   placeholder bars): a split with the money in a tinted panel, vs the same split
   with the breakdown in a tinted panel and each rule lifted onto a white row. */
const EARN_ROWS = [
  ["Commission", "1% of $47,260", "$473"],
  ["Clean 365 signups", "1 × $10", "$10"],
  ["VIP signups", "0 × $100", "$0"],
];
const EARN_STATS = [["Next milestone", "$50,050"], ["Personal best", "$74.1K"], ["Team top", "$74.1K"]];

export const PreviewEarningsLedger = ({ tiny }) => {
  const lw = 104;
  return (
    <Svg>
      <rect x="6" y="6" width={lw} height={H - 12} rx="7" fill="#F7F9FC" />
      <text x="15" y="20" fontSize="6.4" fontWeight="600" fill={FAINT} letterSpacing=".7">TOTAL EARNINGS</text>
      <text x="15" y="43" fontSize="25" fontWeight="600" fill={INK} letterSpacing="-.7">$483</text>
      {!tiny && <text x="15" y="55" fontSize="7.2" fill={FAINT}>Commission + bonuses</text>}
      {!tiny && <line x1="15" y1="64" x2={lw - 3} y2="64" stroke="#E6EBF1" />}
      {!tiny && EARN_STATS.map((s, i) => (
        <g key={s[0]}>
          <text x="15" y={78 + i * 15} fontSize="7.2" fill="#667085">{s[0]}</text>
          <text x={lw - 3} y={78 + i * 15} textAnchor="end" fontSize="8" fontWeight="600" fill={INK}>{s[1]}</text>
        </g>
      ))}
      <text x={lw + 16} y="20" fontSize="6.4" fontWeight="600" fill={FAINT} letterSpacing=".7">HOW IT ADDS UP</text>
      {EARN_ROWS.map((r, i) => (
        <g key={r[0]}>
          {i > 0 && <line x1={lw + 16} y1={26 + i * 22} x2={W - 12} y2={26 + i * 22} stroke="#F1F4F8" />}
          <text x={lw + 16} y={38 + i * 22} fontSize="8.2" fontWeight="500" fill={INK}>{r[0]}</text>
          {!tiny && <text x={lw + 16} y={47 + i * 22} fontSize="6.8" fill={FAINT}>{r[1]}</text>}
          <text x={W - 12} y={38 + i * 22} textAnchor="end" fontSize="8.4" fontWeight="600" fill={i === 2 ? FAINT : INK}>{r[2]}</text>
        </g>
      ))}
      <line x1={lw + 16} y1={H - 26} x2={W - 12} y2={H - 26} stroke={INK} strokeWidth="1.2" />
      <text x={lw + 16} y={H - 13} fontSize="7.6" fill="#344054">Total this month</text>
      <text x={W - 12} y={H - 12} textAnchor="end" fontSize="12" fontWeight="600" fill={INK}>$483</text>
    </Svg>
  );
};

export const PreviewEarningsRows = ({ tiny }) => {
  const lw = 100;
  return (
    <Svg>
      <text x="12" y="20" fontSize="6.4" fontWeight="600" fill={FAINT} letterSpacing=".7">TOTAL EARNINGS</text>
      <text x="12" y="43" fontSize="25" fontWeight="600" fill={INK} letterSpacing="-.7">$483</text>
      {!tiny && <text x="12" y="55" fontSize="7.2" fill={FAINT}>Commission + bonuses</text>}
      {!tiny && <line x1="12" y1="64" x2={lw - 4} y2="64" stroke="#E6EBF1" />}
      {!tiny && EARN_STATS.map((s, i) => (
        <g key={s[0]}>
          <text x="12" y={78 + i * 15} fontSize="7.2" fill="#667085">{s[0]}</text>
          <text x={lw - 4} y={78 + i * 15} textAnchor="end" fontSize="8" fontWeight="600" fill={INK}>{s[1]}</text>
        </g>
      ))}
      <rect x={lw + 4} y="6" width={W - lw - 10} height={H - 12} rx="7" fill="#F7F9FC" />
      <text x={lw + 14} y="20" fontSize="6.4" fontWeight="600" fill={FAINT} letterSpacing=".7">HOW IT ADDS UP</text>
      {EARN_ROWS.map((r, i) => (
        <g key={r[0]}>
          <rect x={lw + 12} y={25 + i * 21} width={W - lw - 26} height="18" rx="4.5" fill="#fff" stroke="#EFF2F6" />
          <text x={lw + 19} y={i === 2 || tiny ? 36 + i * 21 : 34 + i * 21} fontSize="7.6" fontWeight="500" fill={INK}>{r[0]}</text>
          {!tiny && i !== 2 && <text x={lw + 19} y={41 + i * 21} fontSize="6.2" fill={FAINT}>{r[1]}</text>}
          <text x={W - 20} y={37 + i * 21} textAnchor="end" fontSize="8.2" fontWeight="600" fill={i === 2 ? FAINT : INK}>{r[2]}</text>
        </g>
      ))}
      <rect x={lw + 12} y={H - 30} width={W - lw - 26} height="21" rx="5" fill="#EEF2F7" />
      <text x={lw + 19} y={H - 16} fontSize="7.6" fill="#344054">Total this month</text>
      <text x={W - 20} y={H - 15} textAnchor="end" fontSize="11.5" fontWeight="600" fill={INK}>$483</text>
    </Svg>
  );
};

export const PreviewEarnings = ({ tiny }) => {
  const chips = [
    ["Next milestone", "$250"],
    ["Personal best", "$612"],
    ["Team top", "$1,204"],
  ];
  const pad = 11;
  const cw = (W - pad * 2 - 12) / 3;
  return (
    <Svg>
      <text x={pad} y="31" fontSize="26" fontWeight="600" fill={INK} letterSpacing="-.6">$473</text>
      {!tiny && <text x={pad} y="45" fontSize="9.2" fill={FAINT}>Commission + bonuses</text>}
      {chips.map((c, i) => {
        const x = pad + i * (cw + 6);
        return (
          <g key={c[0]}>
            <rect x={x} y="54" width={cw} height="31" rx="6" fill="#F8FAFC" stroke="#EEF1F5" />
            {!tiny && (
              <>
                <text x={x + 7} y="66" fontSize="7.4" fill={FAINT}>{c[0]}</text>
                <text x={x + 7} y="79" fontSize="11.2" fontWeight="600" fill={i === 0 ? BRAND : INK}>{c[1]}</text>
              </>
            )}
          </g>
        );
      })}
      <line x1={pad} y1="93" x2={W - pad} y2="93" stroke="#F1F3F7" />
      {!tiny && (
        <>
          <text x={pad} y="107" fontSize="8.6" fill={FAINT}>Next unlock at $250K</text>
          <text x={W - pad} y="107" textAnchor="end" fontSize="9.6" fontWeight="600" fill={GREEN}>+$250</text>
        </>
      )}
    </Svg>
  );
};

/* A rung is in exactly one of three states, so its colours come from a table
   rather than a stack of ternaries. */
const pick = (state, map) => map[state];

/* $1,000K is not how anyone says it. Matches the live card's compact(). */
const compactK = (k) => (k >= 1000 ? `$${k % 1000 ? (k / 1000).toFixed(1) : k / 1000}M` : `$${k}K`);

/* Milestone scene 1 — the ladder as stepped blocks, filling like a tank.
   Grey until you touch it, green once it is banked, water rising in the rung
   you are on. Static here; the live component animates the crest. */
export const PreviewMilestones = ({ tiny }) => {
  const rungs = [
    [100, 250, "done"],
    [250, 500, "live"],
    [500, 1000, "ahead"],
    [1000, 2000, "ahead"],
  ];
  const pad = 8;
  const base = H - (tiny ? 6 : 18);
  const laneW = (W - pad * 2) / rungs.length;
  const frontW = laneW * 0.78;
  const topOf = (i) => base - (base - 14) * (0.44 + 0.53 * (i / (rungs.length - 1)));
  return (
    <Svg>
      <defs>
        <pattern id="gmbwHatchG" width="9" height="9" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
          <rect width="9" height="9" fill="#D7F0E3" />
          <line x1="0" y1="0" x2="0" y2="9" stroke="#F2FAF6" strokeWidth="4.2" />
        </pattern>
        <pattern id="gmbwHatchN" width="9" height="9" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
          <rect width="9" height="9" fill="#DCE0E6" />
          <line x1="0" y1="0" x2="0" y2="9" stroke="#F0F2F5" strokeWidth="4.2" />
        </pattern>
        <linearGradient id="gmbwTank" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0" stopColor="#2FA96C" />
          <stop offset=".55" stopColor="#4FC489" />
          <stop offset="1" stopColor="#74D9A6" />
        </linearGradient>
      </defs>
      {rungs.map((r, i) => {
        const x = pad + i * laneW;
        const top = topOf(i);
        const last = i === rungs.length - 1;
        const fw = last ? laneW : frontW;
        const done = r[2] === "done";
        const live = r[2] === "live";
        const fy = top + (base - top) * 0.34;
        return (
          <g key={r[0]}>
            {!last && (
              <path
                d={`M${x + fw} ${top} L${pad + (i + 1) * laneW} ${topOf(i + 1)} L${pad + (i + 1) * laneW} ${base} L${x + fw} ${base} Z`}
                fill={pick(r[2], { done: "#B6DFC9", live: "#D2D7DD", ahead: "#C6CBD2" })}
              />
            )}
            <rect
              x={x}
              y={top}
              width={fw}
              height={base - top}
              fill={pick(r[2], { done: "url(#gmbwHatchG)", live: "#E9ECF0", ahead: "url(#gmbwHatchN)" })}
            />
            {live ? (
              <>
                <rect x={x} y={fy} width={fw} height={base - fy} fill="url(#gmbwTank)" />
                <line x1={x} y1={top} x2={x + fw} y2={top} stroke={BRAND} strokeWidth="1.4" strokeDasharray="5 4" opacity=".55" />
                <circle cx={x + fw / 2} cy={fy} r="10" fill={BRAND} stroke="#fff" strokeWidth="2.2" />
                {!tiny && (
                  <text x={x + fw / 2} y={fy + 3.4} textAnchor="middle" fontSize="7.6" fontWeight="700" fill="#fff">JM</text>
                )}
              </>
            ) : (
              <rect x={x + fw * 0.36} y={top - 3.4} width={fw * 0.28} height="5" rx="2.5" fill={done ? "#12A150" : "#AFB6BF"} opacity=".9" />
            )}
            {!tiny && (
              <text
                x={x + fw / 2}
                y={H - 6}
                textAnchor="middle"
                fontSize="8.6"
                fontWeight={live ? 600 : 500}
                fill={pick(r[2], { done: "#7E8B99", live: INK, ahead: "#C2CAD4" })}
              >
                {compactK(r[0])}
              </text>
            )}
          </g>
        );
      })}
    </Svg>
  );
};

/* Milestone scene 2 — the same ladder as stops on one track. */
export const PreviewMilestones2 = ({ tiny }) => {
  const stops = [
    [100, "done"],
    [250, "live"],
    [500, "ahead"],
    [1000, "ahead"],
  ];
  const pad = 26;
  const y = H * 0.56;
  const seg = (W - pad * 2) / (stops.length - 1);
  return (
    <Svg>
      <rect x={pad} y={y - 3} width={W - pad * 2} height="6" rx="3" fill="#E4E8ED" />
      <rect x={pad} y={y - 3} width={seg * 1.33} height="6" rx="3" fill="#4FC489" />
      {stops.map((s, i) => {
        const cx = pad + i * seg;
        const done = s[1] === "done";
        const live = s[1] === "live";
        return (
          <g key={s[0]}>
            {!tiny && (
              <text
                x={cx}
                y={y - 20}
                textAnchor="middle"
                fontSize="10.5"
                fontWeight={live ? 600 : 500}
                fill={pick(s[1], { done: "#7E8B99", live: INK, ahead: "#C9D1DA" })}
              >
                {compactK(s[0])}
              </text>
            )}
            <circle
              cx={cx}
              cy={y}
              r={live ? 9 : 7}
              fill={done ? "#D7F0E3" : "#fff"}
              stroke={pick(s[1], { done: "#12A150", live: BRAND, ahead: "#DDE4EC" })}
              strokeWidth={live ? 3 : 2}
            />
            {live && <circle cx={cx} cy={y} r="3" fill={BRAND} />}
            {!tiny && (
              <text x={cx} y={y + 26} textAnchor="middle" fontSize="8.6" fontWeight="600" fill={done ? GREEN : "#B6BFCA"}>
                {pick(s[1], { done: "$250", live: "$500", ahead: "" })}
              </text>
            )}
          </g>
        );
      })}
    </Svg>
  );
};

/* ── Payroll vs Revenue ──────────────────────────────────────────────────────
   A department, what it costs as a share of revenue, and the notch it is not
   supposed to pass. One row over its goal reads red — that contrast IS the
   card, so the preview has to carry it. */
export const PreviewLabor = ({ tiny }) => {
  const rows = [
    ["Tech", 26, 20],
    ["Office", 3, 5],
    ["Sales", 8, 10],
  ];
  const top = 12;
  const rh = 36;
  const railX = 12;
  const railW = W - 24;
  return (
    <Svg>
      {rows.map(([name, perf, goal], i) => {
        const y = top + rh * i;
        const over = perf > goal;
        const scale = Math.max(perf, goal) * 1.25;
        const at = (v) => (v / scale) * railW;
        return (
          <g key={name}>
            {!tiny && (
              <text x={railX} y={y + 9} fontSize="10.2" fontWeight="500" fill={INK}>
                {name}
              </text>
            )}
            <text
              x={W - railX}
              y={y + 9}
              textAnchor="end"
              fontSize="11"
              fontWeight="600"
              fill={over ? RED : GREEN}
            >
              {`${perf}%`}
            </text>
            <rect x={railX} y={y + 16} width={railW} height="6" rx="3" fill="#EEF1F4" />
            <rect x={railX} y={y + 16} width={at(perf)} height="6" rx="3" fill={over ? RED : GREEN} />
            <line
              x1={railX + at(goal)}
              x2={railX + at(goal)}
              y1={y + 13}
              y2={y + 25}
              stroke={FAINT}
              strokeWidth="2"
              strokeDasharray="2 2"
            />
          </g>
        );
      })}
    </Svg>
  );
};

export const PreviewGoalProgress = ({ tiny }) => {
  const padL = 14;
  const padR = 14;
  const top = tiny ? 14 : 30;
  const base = H - 20;
  const innerW = W - padL - padR;
  const pts = [0.08, 0.22, 0.19, 0.34, 0.3, 0.46, 0.58, 0.52, 0.7, 0.64, 0.82, 0.94];
  const n = pts.length;
  const x = (i) => padL + (i * innerW) / (n - 1);
  const y = (v) => top + (1 - v) * (base - top);
  const linePath = pts.map((v, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L ${x(n - 1).toFixed(1)},${base} L ${x(0).toFixed(1)},${base} Z`;
  const goalY = y(0.86);
  return (
    <Svg>
      {!tiny && (
        <text x={padL} y={16} fontSize="9.5" fontWeight="600" fill={INK}>$34,000<tspan fill={FAINT} fontWeight="500"> of $50,000</tspan></text>
      )}
      <line x1={padL} x2={W - padR} y1={goalY} y2={goalY} stroke={FAINT} strokeWidth="1.3" strokeDasharray="3 3" />
      {!tiny && <text x={W - padR} y={goalY - 4} textAnchor="end" fontSize="7.5" fontWeight="600" fill={FAINT}>GOAL</text>}
      <defs>
        <linearGradient id="gprogPvFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={BRAND} stopOpacity="0.3" />
          <stop offset="100%" stopColor={BRAND} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#gprogPvFill)" />
      <path d={linePath} fill="none" stroke={BRAND} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={x(n - 1)} cy={y(pts[n - 1])} r="3.4" fill="#fff" />
      <circle cx={x(n - 1)} cy={y(pts[n - 1])} r="2.2" fill={BRAND} />
    </Svg>
  );
};

/* ── Revenue, Expenses & Labor ───────────────────────────────────────────────
   Three lines that never touch, and one number that is about to cross a
   dashed limit — the whole point of the card in one glance. Revenue is filled
   because the other two are read as shares of it. */
export const PreviewProfit = ({ tiny }) => {
  const x0 = 12;
  const x1 = W - 12;
  const top = tiny ? 10 : 34;
  const bottom = H - 10;
  const rev = [0.86, 0.8, 0.95, 0.82, 0.76, 0.84, 0.79, 0.81, 0.74, 0.83];
  const exp = [0.47, 0.49, 0.45, 0.44, 0.48, 0.42, 0.5, 0.41, 0.46, 0.44];
  const lab = [0.3, 0.36, 0.33, 0.41, 0.35, 0.29, 0.27, 0.32, 0.3, 0.34];
  const px = (i, n) => x0 + ((x1 - x0) * i) / (n - 1);
  const py = (v) => bottom - (bottom - top) * v;
  const path = (s) => s.map((v, i) => `${i ? "L" : "M"}${px(i, s.length).toFixed(1)} ${py(v).toFixed(1)}`).join(" ");
  const area = `${path(rev)} L${x1} ${bottom} L${x0} ${bottom} Z`;
  return (
    <Svg>
      {!tiny && (
        <>
          <text x={x0} y="14" fontSize="9" fontWeight="500" fill={FAINT}>TOTAL REVENUE</text>
          <text x={x0} y="27" fontSize="13.5" fontWeight="600" fill={INK} letterSpacing="-.3">$469,113</text>
          <text x={x1} y="14" textAnchor="end" fontSize="9" fontWeight="500" fill={FAINT}>NP</text>
          <text x={x1} y="27" textAnchor="end" fontSize="13.5" fontWeight="600" fill={GREEN} letterSpacing="-.3">22%</text>
          <text x={x1 - 52} y="14" textAnchor="end" fontSize="9" fontWeight="500" fill={FAINT}>LABOR</text>
          <text x={x1 - 52} y="27" textAnchor="end" fontSize="13.5" fontWeight="600" fill={RED} letterSpacing="-.3">40%</text>
        </>
      )}
      <path d={area} fill="#378ADD" fillOpacity="0.12" />
      <line x1={x0} x2={x1} y1={py(0.38)} y2={py(0.38)} stroke="#D85A30" strokeWidth="1.4" strokeDasharray="4 3" strokeOpacity="0.7" />
      <path d={path(rev)} fill="none" stroke="#378ADD" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d={path(exp)} fill="none" stroke="#1D9E75" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d={path(lab)} fill="none" stroke="#D85A30" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
};

/* ── Net Profit Bonus ────────────────────────────────────────────────────────
   The whole card in one glance: net profit against a ladder of steps, the first
   two cleared (green) and the third still ahead (dashed) with what it pays. */
export const PreviewNetBonus = ({ tiny }) => {
  const x0 = 12;
  const x1 = W - 12;
  const railY = tiny ? 62 : 74;
  const marks = [0.3, 0.62, 0.94];
  const px = (f) => x0 + (x1 - x0) * f;
  const fill = 0.7;
  const boxW = (x1 - x0 - 12) / 3;
  return (
    <Svg>
      {!tiny && (
        <>
          <text x={x0} y="14" fontSize="9" fontWeight="500" fill={FAINT}>NET PROFIT</text>
          <text x={x0} y="31" fontSize="16" fontWeight="600" fill={INK} letterSpacing="-.4">$27,400</text>
          <text x={x1} y="14" textAnchor="end" fontSize="9" fontWeight="500" fill={FAINT}>PAYING NOW</text>
          <text x={x1} y="31" textAnchor="end" fontSize="16" fontWeight="600" fill={GREEN} letterSpacing="-.4">$250</text>
        </>
      )}
      <rect x={x0} y={railY - 3} width={x1 - x0} height="6" rx="3" fill="#EEF1F4" />
      <rect x={x0} y={railY - 3} width={(x1 - x0) * fill} height="6" rx="3" fill={BRAND} />
      {marks.map((f, i) => (
        <circle
          key={f}
          cx={px(f)}
          cy={railY}
          r="5"
          fill={i < 2 ? BRAND : "#fff"}
          stroke={i < 2 ? "#fff" : "#C9D2DC"}
          strokeWidth="2"
        />
      ))}
      {[0, 1, 2].map((i) => (
        <g key={marks[i]}>
          <rect
            x={x0 + i * (boxW + 6)}
            y={railY + 16}
            width={boxW}
            height={tiny ? 20 : 30}
            rx="6"
            fill={i < 2 ? "rgba(41,158,71,.08)" : "none"}
            stroke={i < 2 ? "rgba(41,158,71,.4)" : "#98A2B3"}
            strokeWidth="1.2"
            strokeDasharray={i < 2 ? undefined : "3 2.5"}
          />
          {!tiny && (
            <text x={x0 + i * (boxW + 6) + 8} y={railY + 36} fontSize="11" fontWeight="600" fill={INK}>
              {["$100", "$250", "$500"][i]}
            </text>
          )}
        </g>
      ))}
    </Svg>
  );
};

/* ── All Department Metrics ──────────────────────────────────────────────────
   A wall of green with the red ones jumping out — that contrast IS the card,
   so the preview has to carry it rather than hint at it. */
export const PreviewDeptMetrics = ({ tiny }) => {
  const rows = [
    ["Sales", 0, [1, 1, 0, 1, 1]],
    ["In-house", 1, [1, 1, 1, 0, 1]],
    ["Field", 1, [0, 1, 1, 1, 1]],
    ["Tech", 0, [1, 0, 1, 1, 0]],
  ];
  const nameW = tiny ? 0 : 58;
  const x0 = 12 + nameW;
  const gap = 5;
  const cellW = (W - 24 - nameW - gap * 4) / 5;
  const top = 12;
  const rh = tiny ? 27 : 27;
  return (
    <Svg>
      {rows.map(([name, depth, cells], r) => {
        const y = top + rh * r;
        return (
          <g key={name}>
            {!tiny && (
              <text
                x={12 + depth * 7}
                y={y + 15}
                fontSize={depth ? "9.4" : "10"}
                fontWeight={depth ? "500" : "600"}
                fill={depth ? "#667085" : INK}
              >
                {name}
              </text>
            )}
            {cells.map((ok, c) => (
              // eslint-disable-next-line react/no-array-index-key
              <g key={c}>
                <rect
                  x={x0 + c * (cellW + gap)}
                  y={y}
                  width={cellW}
                  height={rh - 6}
                  rx="5"
                  fill={ok ? "rgba(18,161,80,.1)" : "rgba(240,68,56,.1)"}
                  stroke={ok ? "rgba(18,161,80,.4)" : "rgba(240,68,56,.4)"}
                  strokeWidth="1.2"
                />
                {!tiny && (
                  <text
                    x={x0 + c * (cellW + gap) + cellW / 2}
                    y={y + 14.5}
                    textAnchor="middle"
                    fontSize="9.6"
                    fontWeight="600"
                    fill={ok ? GREEN : RED}
                  >
                    {ok ? "✓" : "✕"}
                  </text>
                )}
              </g>
            ))}
          </g>
        );
      })}
    </Svg>
  );
};
